<template>
  <div>
    <md-card>
      <assignment-toolbar
          :schedules="schedules"
          :shifts="shifts"
          :schedule="schedule"
          :shift="shift"
          :is-generating="isGeneratingSchedule"
          @update:schedule="handleScheduleUpdate"
          @update:shift="handleShiftUpdate"
          @view:take:on="generateTakeon"
          @generateSchedule="handleGenerateSchedule"
          @go:back="goBack"
      />

      <md-progress-bar md-mode="indeterminate" v-if="pageLoading" />

      <labourer-assignment-table
          ref="assignmentTable"
          :work-points="workPoints"
          :labourers="labourers"
          :error-index="errorIndex"
          :api-validation-errors="apiValidationErrors"
          :is-loading="tableLoading"
          @update:work-points="workPoints = $event"
          @labourer:find="handleLabourerFind"
          @change:labourer="handleChangeLabourer"
          @remove:assignment="handleRemoveAssignment"
      />
    </md-card>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import formMixin from '@/mixins/form-mixin';
import AssignmentToolbar from './components/AssignmentToolbar.vue';
import LabourerAssignmentTable from './components/LabourerAssignmentTable.vue';

export default {
  name: 'ManageLabourerAssignmentPage',
  components: { AssignmentToolbar, LabourerAssignmentTable },
  mixins: [formMixin],
  data() {
    return {
      labourers: [],
      pageLoading: false, // Renamed from isLoading
      tableLoading: false, // Separate loading for table operations
      isGeneratingSchedule: false,
      guidance: {
        type: 'guidances',
        name: null,
        guidance_number: null,
        duration: null,
        start_date: null,
        end_date: null,
        department: {
          id: null,
          type: 'departments',
        },
        relationshipNames: ['department'],
      },
      shiftParams: {
        filter: {
          guidance: ''
        }
      },
      shift: {},
      shifts: [],
      schedule: {},
      schedules: [],
      id: '',
      workPoints: [],
      previousLabourer: {},
      errorIndex: 0,
      searchDebounceTimer: null,
      DEBOUNCE_DELAY: 300
    };
  },
  computed: {
    scheduleDate() {
      return this.schedule && this.schedule.shift_date ? this.schedule.shift_date : null;
    },
    shiftId() {
      return this.shift && this.shift.id ? this.shift.id : null;
    },
    hasWorkPoints() {
      return this.workPoints && this.workPoints.length > 0;
    },
    canGenerateTakeon() {
      return this.schedule &&
          this.schedule.shift_date &&
          this.shift &&
          this.shift.id &&
          !this.pageLoading;
    }
  },
  watch: {
    scheduleDate: {
      handler(newDate, oldDate) {
        if (newDate && newDate !== oldDate) {
          this.generateTakeon();
        }
      }
    },
    shiftId: {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.generateTakeon();
        }
      }
    }
  },
  created() {
    this.initialize();
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },
  methods: {
    async initialize() {
      try {
        this.pageLoading = true;
        await this.fetchGuidance();
        await Promise.all([
          this.fetchWorkplaces(),
          this.fetchShifts(),
          this.fetchSchedules()
        ]);
      } catch (error) {
        console.error('Initialization error:', error);
        this.notifyError('Failed to initialize page. Please refresh and try again.');
      } finally {
        this.pageLoading = false;
      }
    },

    async fetchGuidance() {
      try {
        const id = this.$route.params.id;
        if (!id) {
          throw new Error('Guidance ID is required');
        }
        this.id = id;
        this.shiftParams.filter.guidance = id;

        const params = { include: 'workplaces', id: id };
        await this.$store.dispatch('guidances/get', params);
        this.guidance = this.$store.getters['guidances/guidance'];

        if (!this.guidance) {
          throw new Error('Guidance not found');
        }
      } catch (error) {
        console.error('Error fetching guidance:', error);
        this.notifyError('Failed to load guidance information');
        throw error;
      }
    },

    async fetchWorkplaces() {
      try {
        await this.$store.dispatch('guidances/workplaces', this.id);
        this.workPoints = this.$store.getters['guidances/workplaces'] || [];
      } catch (error) {
        console.error('Error fetching workplaces:', error);
        this.notifyError('Failed to load workplace assignments');
        this.workPoints = [];
      }
    },

    async fetchShifts() {
      try {
        await this.$store.dispatch('shifts/list', this.shiftParams);
        this.shifts = this.$store.getters['shifts/list'] || [];
      } catch (error) {
        console.error('Error fetching shifts:', error);
        this.notifyError('Failed to load shifts');
        this.shifts = [];
      }
    },

    async fetchSchedules() {
      try {
        await this.$store.dispatch('schedules/list', this.shiftParams);
        const schedules = this.$store.getters['schedules/list'] || [];
        this.schedules = this.removeDuplicateSchedules(schedules);

        if (this.schedules.length === 1) {
          this.schedule = this.schedules[0];
        }
      } catch (error) {
        console.error('Error fetching schedules:', error);
        this.notifyError('Failed to load schedules');
        this.schedules = [];
      }
    },

    removeDuplicateSchedules(shiftSchedules) {
      if (!Array.isArray(shiftSchedules) || shiftSchedules.length === 0) {
        return [];
      }

      const seenDates = new Set();
      return shiftSchedules.filter(function(schedule) {
        if (seenDates.has(schedule.shift_date)) {
          return false;
        }
        seenDates.add(schedule.shift_date);
        return true;
      });
    },

    async handleLabourerFind(query, index) {
      console.log('Searching labourer with query:', query, 'for index:', index);

      // Clear previous timeout
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      var self = this;
      this.searchDebounceTimer = setTimeout(async function() {
        try {
          // Set table loading state (this is a data property, not a prop)
          self.tableLoading = true;

          let params = {
            filter: { queryKey: query }
          };

          await self.$store.dispatch('labourers/list', params);
          const results = self.$store.getters['labourers/list'] || [];

          console.log('Search results:', results);

          // Pass results back to the table component
          if (self.$refs.assignmentTable && self.$refs.assignmentTable.setSearchResults) {
            self.$refs.assignmentTable.setSearchResults(results, index);
          }

        } catch (error) {
          console.error('Error searching labourers:', error);
          self.notifyError('Failed to search labourers');

          // Pass empty results on error
          if (self.$refs.assignmentTable && self.$refs.assignmentTable.setSearchResults) {
            self.$refs.assignmentTable.setSearchResults([], index);
          }
        } finally {
          self.tableLoading = false;
        }
      }, this.DEBOUNCE_DELAY);
    },

    async handleChangeLabourer(labourer, index) {
      var self = this;

      if (!labourer || !labourer.id) {
        this.notifyError('Invalid labourer selection');
        return;
      }

      var schedule = this.workPoints[index];
      if (!schedule) {
        this.notifyError('Schedule not found');
        return;
      }

      var previousLabourerData = schedule.labourer;

      try {
        this.tableLoading = true;

        var updatedSchedule = Object.assign({}, schedule, {
          labourer_id: labourer.id,
          type: 'labourerSchedules'
        });

        if (schedule.uuid) {
          updatedSchedule.id = schedule.uuid;
          await this.$store.dispatch('labourerSchedules/update', updatedSchedule);

          var savedSchedule = this.$store.getters['labourerSchedules/labourerSchedule'];
          this.workPoints[index].labourer = labourer;
          this.workPoints[index].labourer_id = labourer.id;

          this.notifySuccess(labourer.name + ' (' + labourer.labourer_number + ') assigned to ' + savedSchedule.takeon_number)

        }
      } catch (error) {
        console.error('Error changing labourer:', error);

        if (previousLabourerData) {
          this.workPoints[index].labourer = previousLabourerData;
          this.workPoints[index].labourer_id = previousLabourerData.id;
        }
        this.notifyException(error);

        this.handleApiError(error);
      } finally {
        this.tableLoading = false;
      }
    },

    async handleRemoveAssignment(index) {
      var labourerSchedule = this.workPoints[index];
      var self = this;

      if (!labourerSchedule || !labourerSchedule.labourer_id) {
        await this.notifyError('No labourer assigned to this position');
        return;
      }

      var result = await Swal.fire({
        title: 'Remove Labourer Assignment?',
        text: 'You are about to remove ' + labourerSchedule.labourer.name + '. Takeon Number: ' + labourerSchedule.takeon_number,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove it!',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6'
      });

      if (!result.value) return;

      try {
        this.tableLoading = true;

        var updatedSchedule = Object.assign({}, labourerSchedule, {
          labourer_id: null,
          type: 'labourerSchedules'
        });

        if (labourerSchedule.uuid) {
          updatedSchedule.id = labourerSchedule.uuid;
          await this.$store.dispatch('labourerSchedules/update', updatedSchedule);

          this.workPoints[index].labourer_id = null;
          this.workPoints[index].labourer = null;

          await Swal.fire(
              'Removed!',
              labourerSchedule.labourer.name + ' has been removed successfully.',
              'success'
          );
        }
      } catch (error) {
        console.error('Error removing assignment:', error);
        this.handleApiError(error);
      } finally {
        this.tableLoading = false;
      }
    },

    async handleGenerateSchedule() {
      var self = this;

      if (this.isGeneratingSchedule) return;

      var result = await Swal.fire({
        title: 'Generate Schedule?',
        text: 'This will create a new schedule for this guidance. Continue?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, generate',
        cancelButtonText: 'Cancel'
      });

      if (!result.value) return;

      try {
        this.isGeneratingSchedule = true;
        this.pageLoading = true;

        await this.$store.dispatch('guidances/generateSchedule', this.id);
        await this.fetchSchedules();

        this.notifySuccess('Schedule generated successfully');
      } catch (error) {
        console.error('Error generating schedule:', error);
        this.handleApiError(error);
      } finally {
        this.isGeneratingSchedule = false;
        this.pageLoading = false;
      }
    },

    async generateTakeon() {
      var self = this;

      if (!this.canGenerateTakeon) {
        if (!this.schedule || !this.schedule.shift_date) {
          this.notifyWarning('Please select a schedule date');
        } else if (!this.shift || !this.shift.id) {
          this.notifyWarning('Please select a shift');
        }
        return;
      }

      try {
        this.pageLoading = true;

        var payload = {
          shift_date: this.schedule.shift_date,
          schedule: this.schedule.id,
          shift: this.shift.id,
          id: this.id
        };

        await this.$store.dispatch('guidances/generateTakeon', payload);

        var updatedWorkPoints = this.$store.getters['guidances/workplaces'];
        this.workPoints = updatedWorkPoints || [];

        if (!this.workPoints.length) {
          this.notifyInfo('No work points found. Please refresh the page.');
        } else {
          this.notifySuccess('Takeon numbers generated successfully');
        }
      } catch (error) {
        console.error('Error generating takeon:', error);
        this.handleApiError(error);
      } finally {
        this.pageLoading = false;
      }
    },

    handleScheduleUpdate(schedule) {
      this.schedule = schedule;
    },

    handleShiftUpdate(shift) {
      this.shift = shift;
    },

    handleApiError(error) {
      if (error && error.response && error.response.data && error.response.data.errors && error.response.data.errors.length) {
        var apiError = error.response.data.errors[0];
        this.notifyError(apiError.title || 'An error occurred');
        this.setApiValidation(error.response.data.errors);
      } else {
        this.notifyError('An unexpected error occurred. Please try again.');
      }
    },

    goBack() {
      var id = this.guidance && this.guidance.id ? this.guidance.id : null;
      if (id) {
        this.$router.push({ name: 'View Guidance', params: { id: id } });
      } else {
        this.$router.go(-1);
      }
    },

    notifySuccess(message) {
      this.$store.dispatch('alerts/success', message);
    },

    notifyError(message) {
      this.$store.dispatch('alerts/error', message);
    },

    notifyWarning(message) {
      this.$store.dispatch('alerts/warning', message);
    },

    notifyInfo(message) {
      this.$store.dispatch('alerts/info', message);
    }
  }
};
</script>

<style scoped>
.md-progress-bar {
  position: sticky;
  top: 0;
  z-index: 100;
}

/* Add smooth transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>